'use client';

/* ──────────────────────────────────────────────
 *  AvailabilityPicker — étape créneau
 *  Sélecteurs (lieu express, membre, ressource) au-dessus de la
 *  grille, rappel des heures d'une promotion, prochain créneau
 *  quand la semaine est vide (spec Parcours P3, Promotions lot 4).
 * ────────────────────────────────────────────── */

import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDurationFromMinutes } from '@cfixe-monorepo/shared';
import { useBooking } from '../../context/BookingContext';
import { hasOtherPlace } from '../../context/booking-machine';
import { type StepNav, useRegisterStepNav } from '../../context/StepNavContext';
import type { BookableNextSlot, BookableSlot, MemberAssignment } from '../../types';
import { SlotWeekPicker } from '../shared/SlotWeekPicker';
import { CallProButton } from '../shared/CallProButton';
import { getPreferredSlotDate, getSlotDate } from '../shared/slot-picker-utils';
import { parseLocalDate, slotWindowEnd, slotWindowStart, toISODate } from '../../utils/date-helpers';
import { SlotSelectors } from './SlotSelectors';

/* ── Types ─────────────────────────────────── */

interface SlotRequestItem {
  prestation_id: number;
  duration: number;
  team_member_id?: number;
  team_member_ids?: number[];
  pro_place_id: number;
  quantity: number;
  selected_price_index: number;
  selected_supplement_ids: number[];
  pro_resource_ids?: number[];
  party_size?: number;
}

/* ── Prochain créneau d'une semaine vide ───── */

function NextSlotPanel({
  requestKey,
  startDate,
  payload,
  onJump,
}: {
  /* Une seule recherche par semaine et par sélection : la clé change, la recherche repart. */
  requestKey: string;
  startDate: string;
  payload: SlotRequestItem[];
  onJump: (slot: BookableSlot) => void;
}): JSX.Element {
  const { ctx, send, callbacks, labels } = useBooking();
  const [result, setResult] = useState<{ key: string; next?: BookableNextSlot } | undefined>(undefined);
  const find = callbacks.onFindNextSlot;

  useEffect((): (() => void) => {
    let cancelled = false;
    if (!find) return (): void => undefined;
    find({ start_date: startDate, slots: payload })
      .then((next): void => {
        if (!cancelled) setResult({ key: requestKey, next });
      })
      .catch((): void => {
        if (!cancelled) setResult({ key: requestKey });
      });
    return (): void => {
      cancelled = true;
    };
  }, [requestKey]);

  const current = result?.key === requestKey ? result : undefined;
  const nextSlot = current?.next?.slot;
  const memberChosen = ctx.memberAssignments.some((a): boolean => a.memberId !== undefined);
  const resetMembers = (): void =>
    send({
      type: 'SET_MEMBERS',
      assignments: ctx.memberAssignments.map(
        (a): MemberAssignment => ({ ...a, memberId: undefined, memberIds: undefined }),
      ),
    });

  return (
    <div className="bt-next-slot" role="status">
      <p className="bt-next-slot__title">{labels.no_slot_this_week}</p>
      {find && !current ? <p className="bt-next-slot__searching">{labels.next_slot_searching}</p> : null}
      {nextSlot && current?.next?.slot_start_formatted ? (
        <button type="button" className="bt-cta bt-cta--small" onClick={(): void => onJump(nextSlot)}>
          {labels.next_slot.replace('{date}', current.next.slot_start_formatted)}
        </button>
      ) : null}
      {current && !nextSlot && current.next?.horizon_end_formatted ? (
        <p className="bt-next-slot__none">
          {labels.no_slot_until.replace('{date}', current.next.horizon_end_formatted)}
        </p>
      ) : null}
      <div className="bt-next-slot__exits">
        {memberChosen ? (
          <button type="button" className="bt-chip" onClick={resetMembers}>
            {labels.exit_no_preference}
          </button>
        ) : null}
        {hasOtherPlace(ctx) && ctx.entryMode !== 'express' ? (
          <button type="button" className="bt-chip" onClick={(): void => send({ type: 'EDIT_STEP', step: 'place' })}>
            {labels.exit_other_place}
          </button>
        ) : null}
        {ctx.cartItems.length > 1 ? (
          <button type="button" className="bt-chip" onClick={(): void => send({ type: 'EDIT_STEP', step: 'services' })}>
            {labels.exit_remove_prestation}
          </button>
        ) : null}
        <CallProButton />
      </div>
    </div>
  );
}

/* ── Component ──────────────────────────────── */

export function AvailabilityPicker(): JSX.Element {
  const { ctx, send, callbacks, labels, locale, goBack, initialWeekStart, initialDate } = useBooking();
  const {
    cartItems,
    selectedPlaceId,
    memberAssignments,
    resourceAssignments,
    slots,
    slotsLoading,
    selectedSlot,
    slotWeekStart,
    slotNotice,
    entryMode,
  } = ctx;

  /* La grille avance par fenêtres de 7 jours qui partent d'aujourd'hui (aujourd'hui, + 7, + 14…), jamais
     du lundi (bug 34). « Semaine » ci-dessous désigne cette fenêtre. Aujourd'hui figé au montage. */
  const [today] = useState((): string => toISODate(new Date()));

  /* The slot listing only scans a 7-day window per request, while `is_bookable` is probed over a
     much wider horizon. For a fully-booked pro the first bookable date can therefore be weeks away:
     opening the grid on the current window would show it empty even though the service IS bookable.
     `first_bookable_date` (machine value from the probe) tells us which window to open on.
     La date de la DURÉE choisie prime quand la sonde l'a produite : une durée longue commence plus
     tard qu'une courte, et la date prestation (ancrée sur la plus courte) ouvrirait trop tôt.
     Latest of the cart, since every item must fit in the same window. Never moves backwards. */
  const firstBookableWeek = useMemo((): string | undefined => {
    const dates = cartItems
      .map((item): string | undefined => item.selectedPrice.first_bookable_date ?? item.prestation.first_bookable_date)
      .filter((date): date is string => date !== undefined);
    if (dates.length === 0) return undefined;
    const latest = dates.reduce((left, right): string => (left > right ? left : right));
    const window = slotWindowStart(latest, today);
    return window > today ? window : undefined;
  }, [cartItems, today]);

  /* Fenêtre demandée par un lien profond (`?d=`), jamais AVANT la première fenêtre réservable :
     ouvrir plus tôt afficherait une grille vide alors que la prestation est réservable. */
  const deepLinkWeek = useMemo((): string | undefined => {
    if (!initialWeekStart) return undefined;
    if (!firstBookableWeek) return initialWeekStart;
    return initialWeekStart > firstBookableWeek ? initialWeekStart : firstBookableWeek;
  }, [initialWeekStart, firstBookableWeek]);

  /* Current window start — the visitor's own navigation, else the deep link, else the first bookable
     window, else today */
  const currentWeekStart = useMemo(
    (): string => slotWeekStart ?? deepLinkWeek ?? firstBookableWeek ?? today,
    [slotWeekStart, deepLinkWeek, firstBookableWeek, today],
  );

  /* Track selected day within the week */
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);

  /* Le jour demandé par le lien profond n'est honoré qu'au PREMIER chargement de créneaux : passé
     ce point, la navigation du visiteur (changement de semaine, de jour) doit primer, sinon la
     grille le ramènerait sans cesse sur le jour du lien. Le saut « Prochain créneau » pose, lui,
     le jour à ouvrir au chargement suivant. */
  const deepLinkDateConsumedRef = useRef(false);
  const pendingDateRef = useRef<string | undefined>(undefined);

  /* Auto-select first day with slots when slots load */
  const prevSlotsRef = useRef(slots);
  useEffect((): void => {
    if (slots !== prevSlotsRef.current) {
      prevSlotsRef.current = slots;
      if (slots.length > 0) {
        const preferredDate = pendingDateRef.current ?? (deepLinkDateConsumedRef.current ? undefined : initialDate);
        deepLinkDateConsumedRef.current = true;
        pendingDateRef.current = undefined;
        const firstDate = getPreferredSlotDate(slots, currentWeekStart, preferredDate);
        setSelectedDate(firstDate);
        /* The backend clamps the search to now + the pro's minimum booking delay,
           so slots can land beyond the requested week: move the grid onto them.
           Do NOT pre-arm the load key here (audit P2 #7): the response for week N only
           carries the tail of the 7-day window inside week N+K — the rest of that week
           would show as closed. Let the load effect refetch the full target week; it
           converges (the target is monotonic and bounded by the horizon). */
        if (firstDate !== undefined && (firstDate < currentWeekStart || firstDate > slotWindowEnd(currentWeekStart))) {
          send({ type: 'SET_WEEK_START', weekStart: slotWindowStart(firstDate, today) });
        }
      } else {
        setSelectedDate(undefined);
      }
    }
  }, [slots, currentWeekStart, initialDate, today, send]);

  /* Build the slot request payload from cart state */
  const slotRequestPayload = useMemo(
    (): SlotRequestItem[] =>
      cartItems.map((item, i): SlotRequestItem => {
        const assignment = memberAssignments.find((a): boolean => a.cartIndex === i);
        const resAssignment = resourceAssignments.find((a): boolean => a.cartIndex === i);
        const requiredMembers = Math.max(1, item.prestation.required_team_members_count ?? 1);
        const explicitMemberIds = assignment?.memberIds?.filter((id): boolean => id !== undefined);
        const supplementDurationMinutes = item.supplements
          .filter((s): boolean => s.selected)
          .reduce((sum, s): number => sum + (s.duration_minutes ?? 0) * item.quantity, 0);
        return {
          prestation_id: item.prestation.prestation_id,
          duration: item.selectedPrice.duration_minutes + supplementDurationMinutes,
          // For N>=2 prestations, pass the explicit member subset (if any) via team_member_ids
          // so the backend restricts its search. Leave team_member_id unset for N>=2.
          team_member_id: requiredMembers >= 2 ? undefined : assignment?.memberId,
          team_member_ids:
            requiredMembers >= 2 && explicitMemberIds && explicitMemberIds.length > 0 ? explicitMemberIds : undefined,
          pro_place_id: selectedPlaceId ?? 0,
          quantity: item.quantity,
          selected_price_index: item.selectedPriceIndex,
          selected_supplement_ids: item.supplements
            .filter((s): boolean => s.selected)
            .map((s): number => s.supplement_id),
          pro_resource_ids: resAssignment?.pro_resource_id !== undefined ? [resAssignment.pro_resource_id] : undefined,
          party_size: item.party_size,
        };
      }),
    [cartItems, memberAssignments, resourceAssignments, selectedPlaceId],
  );

  const hasSessionItem = useMemo(
    (): boolean => cartItems.some((item): boolean => item.prestation.is_session === true),
    [cartItems],
  );

  /* Clé de la grille chargée : la fenêtre ET la sélection (lieu, membres, ressources, choisis sur cette
     étape). Un changement de l'une ou l'autre recharge les créneaux. */
  const requestKey = `${currentWeekStart}|${JSON.stringify(slotRequestPayload)}`;
  const loadedKeyRef = useRef<string | null>(null);
  const startIso = useMemo((): string => parseLocalDate(currentWeekStart).toISOString(), [currentWeekStart]);

  /* Clé de la dernière réponse de `/bookable`. Tant qu'elle diffère de la clé demandée, la grille n'est « pas
     encore chargée » — un état distinct de « chargée et vide ». Le panneau « prochain créneau » n'apparaît
     qu'après une réponse vide (décision n° 9) : affiché dès le premier rendu, il lançait `bookable/next` à
     chaque arrivée sur l'étape, deux fois sur une semaine vide, sur une route limitée à 30 appels par minute. */
  const [resolvedKey, setResolvedKey] = useState<string | undefined>(undefined);
  const weekResolved = resolvedKey === requestKey;

  useEffect((): void => {
    if (loadedKeyRef.current === requestKey) return;
    loadedKeyRef.current = requestKey;

    send({ type: 'SLOTS_LOADING' });

    callbacks
      .onLoadSlots({
        start_date: startIso,
        slots: slotRequestPayload,
        // Séance au panier ⇒ demander AUSSI les créneaux sous-capacité/complets (grisés à l'écran).
        include_session_slots_below_party_size: hasSessionItem || undefined,
      })
      .then((loadedSlots): void => {
        // Réponse d'une sélection dépassée (le client a changé de membre entre-temps) : ignorée.
        if (loadedKeyRef.current !== requestKey) return;
        send({ type: 'SLOTS_LOADED', slots: loadedSlots });
        setResolvedKey(requestKey);
      })
      .catch((err: unknown): void => {
        console.error('[BookingTunnel] Failed to load slots', err);
        if (loadedKeyRef.current !== requestKey) return;
        send({ type: 'SLOTS_LOADED', slots: [] });
        setResolvedKey(requestKey);
      });
  }, [requestKey, startIso, slotRequestPayload, hasSessionItem, callbacks, send]);

  /* Taille du plus grand groupe demandé — sert UNIQUEMENT au libellé « pas assez de places
     pour N personnes ». Le grisage, lui, vient du verdict backend (below_requested_party) :
     le déduire ici d'un compteur dont l'unité varie annonçait « pour 1 personne » sur un
     item libre-service réservé à 4. */
  const maxRequestedPartySize = useMemo(
    (): number => cartItems.reduce((max, item): number => Math.max(max, item.party_size ?? 1), 1),
    [cartItems],
  );

  const slotPickerLabels = useMemo(
    (): {
      previousWeek: string;
      nextWeek: string;
      morning: string;
      afternoon: string;
      evening: string;
      noSlots: string;
      loading: string;
      placesOne: string;
      placesOther: string;
      full: string;
      insufficientForParty: string;
    } => ({
      previousWeek: labels.week_prev,
      nextWeek: labels.week_next,
      morning: labels.morning,
      afternoon: labels.afternoon,
      evening: labels.evening,
      noSlots: labels.no_slots,
      loading: labels.slots_loading,
      placesOne: labels.slot_places_one,
      placesOther: labels.slot_places_other,
      full: labels.slot_full,
      insufficientForParty: labels.slot_insufficient_places.replace('{count}', String(maxRequestedPartySize)),
    }),
    [
      labels.afternoon,
      labels.week_prev,
      labels.week_next,
      labels.evening,
      labels.morning,
      labels.no_slots,
      labels.slots_loading,
      labels.slot_places_one,
      labels.slot_places_other,
      labels.slot_full,
      labels.slot_insufficient_places,
      maxRequestedPartySize,
    ],
  );

  /* Rappel des heures d'une promotion pour le jour choisi (lot 4) : « Happy hours −15 % de 10h00 à 12h00 ». */
  const dayPromotion = useMemo((): string | undefined => {
    const promoted = slots.find(
      (slot): boolean => getSlotDate(slot) === selectedDate && slot.promotion_name !== undefined,
    );
    if (!promoted?.promotion_name || !promoted.promotion_label) return undefined;
    return labels.day_promotion
      .replace('{name}', promoted.promotion_name)
      .replace('{value}', promoted.promotion_label)
      .replace('{hours}', promoted.promotion_hours_label ?? '')
      .trim();
  }, [slots, selectedDate, labels.day_promotion]);

  /* ── Handlers ──────────────────────────────── */

  const handleWeekChange = useCallback(
    (weekStart: string): void => {
      send({ type: 'SET_WEEK_START', weekStart });
      setSelectedDate(undefined);
    },
    [send],
  );

  const handleJumpToSlot = useCallback(
    (slot: BookableSlot): void => {
      const date = getSlotDate(slot);
      if (!date) return;
      pendingDateRef.current = date;
      send({ type: 'SET_WEEK_START', weekStart: slotWindowStart(date, today) });
      setSelectedDate(undefined);
    },
    [send, today],
  );

  const handleSelectSlot = useCallback(
    (slot: BookableSlot): void => {
      send({ type: 'SELECT_SLOT', slot });
    },
    [send],
  );

  const handleConfirm = useCallback((): void => {
    send({ type: 'CONFIRM_SLOT' });
  }, [send]);

  useRegisterStepNav(
    useMemo(
      (): StepNav => ({
        onBack: goBack,
        primary: {
          label: labels.continue,
          onClick: handleConfirm,
          disabled: slotsLoading || !selectedSlot,
        },
      }),
      [goBack, labels.continue, handleConfirm, slotsLoading, selectedSlot],
    ),
  );

  /* ── Render ────────────────────────────────── */

  /* Express : la prestation rappelée en tête, « modifier » rouvre sa carte (spec Parcours § 5.1). */
  const expressItem = entryMode === 'express' && cartItems.length === 1 ? cartItems[0] : undefined;
  const expressSummary = expressItem
    ? [
        expressItem.prestation.name,
        [
          expressItem.selectedPrice.label,
          expressItem.selectedPrice.duration_minutes
            ? formatDurationFromMinutes(expressItem.selectedPrice.duration_minutes)
            : undefined,
        ]
          .filter(Boolean)
          .join(' '),
        expressItem.selectedPrice.price_string,
      ]
        .filter(Boolean)
        .join(' · ')
    : undefined;

  return (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{labels.choose_slot}</h2>
      </div>

      {expressSummary ? (
        <p className="bt-express-item">
          <span className="bt-express-item__text">{expressSummary}</span>
          <button
            type="button"
            className="bt-link-subtle bt-express-item__edit"
            aria-label={labels.edit_aria.replace('{item}', expressItem?.prestation.name ?? '')}
            onClick={(): void => send({ type: 'EDIT_STEP', step: 'services' })}
          >
            {labels.edit}
          </button>
        </p>
      ) : null}

      {slotNotice ? (
        <div className="bt-banner bt-banner--warning" role="alert">
          {slotNotice === 'taken' ? labels.slot_taken_notice : labels.slot_released_notice}
        </div>
      ) : null}

      <SlotSelectors />

      <SlotWeekPicker
        slots={slots}
        selectedSlot={selectedSlot}
        selectedDate={selectedDate}
        weekStart={currentWeekStart}
        labels={slotPickerLabels}
        locale={locale}
        loading={slotsLoading || !weekResolved}
        onSelectDate={setSelectedDate}
        onSelectSlot={handleSelectSlot}
        onWeekChange={handleWeekChange}
        dayHeader={dayPromotion ? <p className="bt-timeslots__promotion">{dayPromotion}</p> : undefined}
        emptyContent={
          /* Seulement une fois la semaine demandée chargée et vide : une recherche du prochain créneau, pas deux. */
          weekResolved ? (
            <NextSlotPanel
              requestKey={requestKey}
              startDate={startIso}
              payload={slotRequestPayload}
              onJump={handleJumpToSlot}
            />
          ) : undefined
        }
      />
    </div>
  );
}
